import { T } from 'gt-react';
import { WarningCircleIcon } from '@phosphor-icons/react';

import { ReasonChips } from './ReasonChips';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { FlaggedRdStockRow } from '@/lib/daily-reports/types';

export function RdStockMappedTable({ rows }: { rows: FlaggedRdStockRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20">
        <WarningCircleIcon
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
          weight="fill"
        />
        <p>
          <T context="STOCK_V rows with a Vera DC are stock-without-item discrepancies">
            Stock with no item master at this DC. Filter by DC (K↔A, D↔B, O↔C,
            E↔F, M↔H, V↔J), not letter equality. Gently does not write back to
            AS/400.
          </T>
        </p>
      </div>
      <RdStockTable rows={rows} showReasons={false} />
    </div>
  );
}

export function SupersededMappedTable({ rows }: { rows: FlaggedRdStockRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20">
        <WarningCircleIcon
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
          weight="fill"
        />
        <p>
          <T context="STOCKS_V same-DC Vera pairs are superseded items with stock">
            Superseded item with stock at this DC. Filter by DC (K↔A, D↔B, O↔C,
            E↔F, M↔H, V↔J), not letter equality. Gently does not write back to
            AS/400.
          </T>
        </p>
      </div>
      <RdStockTable rows={rows} showReasons={false} />
    </div>
  );
}

export function SupersededUnmatchedTable({
  rows,
}: {
  rows: FlaggedRdStockRow[];
}) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20">
        <WarningCircleIcon
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
          weight="fill"
        />
        <p>
          <T context="STOCKS_V rows without a Vera DC match, including blank and non-RD">
            STOCKS_V rows without a Vera DC match. See Reasons for blank
            location, non-RD, unpaired I, letter equality, or a pair that is not
            in the DC map. Gently does not write back to AS/400.
          </T>
        </p>
      </div>
      <RdStockTable rows={rows} showReasons />
    </div>
  );
}

export function RdStockUnmatchedTable({ rows }: { rows: FlaggedRdStockRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-50/50 p-3 text-sm dark:bg-amber-950/20">
        <WarningCircleIcon
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
          weight="fill"
        />
        <p>
          <T context="instruction after unmatched STOCK_V rows are flagged">
            Real unmatched RD stock — no Vera partner, unpaired I, or a
            stock/item pair that is not in the DC map. Gently does not write
            back to AS/400.
          </T>
        </p>
      </div>
      <RdStockTable rows={rows} showReasons />
    </div>
  );
}

function RdStockTable({
  rows,
  showReasons,
}: {
  rows: FlaggedRdStockRow[];
  showReasons: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <T context="column header for the source daily report file name">
                File
              </T>
            </TableHead>
            <TableHead>
              <T context="column header for STOCK_V worksheet name">Sheet</T>
            </TableHead>
            <TableHead>
              <T context="column header for PRODUCT NUMBER on a STOCK_V row">
                Product
              </T>
            </TableHead>
            <TableHead>
              <T context="column header for STOCK ACCESS LOCATION">Stock</T>
            </TableHead>
            <TableHead>
              <T context="column header for the mapped item-master location">
                Item master
              </T>
            </TableHead>
            <TableHead>
              <T context="column header for the Rexel DC name">DC</T>
            </TableHead>
            {showReasons ? (
              <TableHead>
                <T context="column header listing why a STOCK_V row was unmatched">
                  Reasons
                </T>
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.fileName}-${row.sheetName}-${row.sheetRow}-${row.productNumber}`}
            >
              <TableCell className="max-w-[10rem] truncate text-xs">
                {row.fileName}
              </TableCell>
              <TableCell className="max-w-[10rem] truncate text-xs">
                {row.sheetName}
              </TableCell>
              <TableCell className="font-medium">{row.productNumber}</TableCell>
              <TableCell>{row.stockLocation}</TableCell>
              <TableCell>{row.itemMasterLocation}</TableCell>
              <TableCell>{row.dc}</TableCell>
              {showReasons ? (
                <TableCell>
                  <ReasonChips reasons={row.reasons} />
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
